import React, { useMemo, useCallback } from "react";
import { User, Building2, Calendar, Shield, PawPrint } from "lucide-react";
import { HouseContract } from "components/icons/customIcons";
import { ContractDetailsGlobal, ContractType, GenericColors } from "api/contract";
import { BranchCode } from "api/risk";
import { BadgeVariant, EntityDetailsHeader, EntityDetailsHeaderConfig } from "components/display/EntityDetailsHeader";
import { useLocation, useNavigate } from "react-router-dom";

export interface ContractDetailsHeaderProps {
  contractDetails: ContractDetailsGlobal;
}

const ContractDetailsHeader: React.FC<ContractDetailsHeaderProps> = ({
  contractDetails
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Le header est partagé entre l'espace pro et le back-office admin :
  // le contrat collectif parent n'a pas la même route selon le contexte
  const isBackOffice = location.pathname.startsWith("/back-office");

  // Navigation handlers
  const handleNavigateToAssure = useCallback((id: string) => {
    navigate(`/mes-clients/${id}`)
  }, [navigate]);

  const handleNavigateToContratParent = useCallback((id: string) => {
    navigate(isBackOffice
      ? `/back-office/contrats-groupe/${id}`
      : `/mes-contrats-collectifs/${id}`)
  }, [navigate, isBackOffice]);

  // Mapper GenericColors vers BadgeVariant
  const mapColorToVariant = (color?: GenericColors): BadgeVariant => {
    switch (color) {
      case GenericColors.Green:
        return "green";
      case GenericColors.Orange:
        return "orange";
      case GenericColors.Red:
        return "red";
      default:
        return "gray";
    }
  };

  // Construire la configuration
  const config: EntityDetailsHeaderConfig = useMemo(() => {
    const sections = [];

    // Section PRODUIT (toujours présente)
    sections.push({
      icon: Building2,
      label: "PRODUIT",
      value: contractDetails.productLabel,
      disabled: true
    });

    // Section ASSURÉ (uniquement pour les contrats non-groupe et si l'insured
    // est résolu côté backend — pas le cas pour les contrats PR_ANI créés via
    // le parcours public anonyme tant que la PP est en mode "anonyme" sans
    // AbpUser rattaché)
    if (contractDetails.contractType !== ContractType.GROUP && contractDetails.insured) {
      sections.push({
        icon: User,
        label: contractDetails.contractType === ContractType.ALIMONY
          ? "ASSURÉ - ADHÉRENT"
          : "ASSURÉ",
        value: contractDetails.insured.label,
        onClick: () => handleNavigateToAssure(contractDetails.insured.id),
        showExternalLink: true
      });
    }

    // Section SOUSCRIPTEUR (contrat aliment) : lien direct vers le contrat
    // collectif parent, sans afficher le nom du souscripteur
    const groupContract = contractDetails.groupContract;
    if (groupContract) {
      sections.push({
        icon: Building2,
        label: "SOUSCRIPTEUR - COLLECTIF",
        value: groupContract.label,
        onClick: () => handleNavigateToContratParent(groupContract.id),
        showExternalLink: true
      });
    }

    // Libellé du contrat
    const getContractLabel = () => {
      switch (contractDetails.contractType) {
        case ContractType.GROUP:
          return "Contrat de groupe";
        case ContractType.INDIVIDUAL:
          return "Contrat Individuel";
        case ContractType.ALIMONY:
          return "Contrat Aliment";
        default:
          return "Contrat";
      }
    };

    const isPet = contractDetails.branchCode === BranchCode.Pet;

    return {
      icon: isPet
        ? <PawPrint className="w-full h-full text-primary" />
        : <HouseContract className="w-full text-primary" />,
      title: getContractLabel(),
      subtitle: contractDetails.contractIdentifier,
      infoSections: sections,
      badge: {
        label: contractDetails.status || "Non défini",
        variant: mapColorToVariant(contractDetails.statusColor),
        icon: Shield
      },
      metadata: {
        icon: Calendar,
        text: `Créé le ${new Date(contractDetails.creationDate).toLocaleDateString('fr-FR')}`
      }
    };
  }, [contractDetails, handleNavigateToAssure, handleNavigateToContratParent]);

  return <EntityDetailsHeader config={config} />;
};

export default ContractDetailsHeader;
